import type { ScheduleAiTheme } from 'src/theme/schedule-ai-overrides';

import { Avatar, Tooltip, useTheme, Typography } from '@mui/material';

import { Iconify } from 'src/components/iconify';

import { getNameInitials } from 'src/utils/get-name-initials';

import { InspectorType } from 'src/types/inspector';

interface Props {
  id?: number | string;
  name?: string;
  type?: InspectorType;
  variant?: 'internal' | 'external';
  size?: 'mini' | 'medium' | 'large';
  avatarBorderWidth?: number;
  disableGroupOffset?: boolean;
  onClick?: () => void;
}

export const UserIcon = ({
  id,
  name,
  type,
  variant = 'internal',
  size = 'medium',
  avatarBorderWidth,
  disableGroupOffset = false,
  onClick,
}: Props) => {
  const theme: ScheduleAiTheme = useTheme();
  const initials = getNameInitials(name);

  // Determine the UI variant based on type or fallback to provided variant
  let uiVariant = variant;
  if (type) {
    switch (type) {
      case InspectorType.EXTERNAL:
        uiVariant = 'external';
        break;
      case InspectorType.INTERNAL:
        uiVariant = 'internal';
        break;
      default:
        // Keep the default variant if type is not recognized
        break;
    }
  }

  // Ensure we have a valid variant that exists in the theme
  if (!theme.userIcon[uiVariant]) {
    uiVariant = 'internal';
  }

  return name ? (
    <Tooltip title={name ? `${name} [${type?.toLowerCase()}]` : ''} key={id}>
      <Avatar
        sx={{
          bgcolor: theme.userIcon[uiVariant].bgColor,
          '.MuiAvatarGroup-root &.MuiAvatar-root, &.MuiAvatar-root': {
            borderWidth: avatarBorderWidth ?? 2,
            borderStyle: theme.userIcon[uiVariant].borderStyle,
            borderColor: theme.userIcon[uiVariant].borderColor,
          },
          ...(size === 'mini' ? { width: 24, height: 24 } : {}),
          ...(size === 'large' ? { width: 64, height: 64 } : {}),
          ml: disableGroupOffset ? 0 : 1,
        }}
        onClick={onClick}
      >
        <Typography
          variant="body2"
          color={theme.userIcon[uiVariant].textColor}
          sx={{
            textTransform: 'uppercase',
            ...(size === 'mini' && {
              letterSpacing: '0.01em',
            }),
          }}
        >
          {initials}
        </Typography>
      </Avatar>
    </Tooltip>
  ) : (
    <Avatar
      sx={{
        bgcolor: 'transparent',
        border: 'none',
      }}
      onClick={onClick}
    >
      <Iconify icon="material-symbols:person-add-rounded" color="text.secondary" />
    </Avatar>
  );
};
